import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useFormik } from "formik";
import "./CandidateFilterDrawer.css";
import { DynamicContext } from "../../../Context/DynamicContext";
import DynamicTextField from "../../../Common/Textfield/Textfield";
import DynamicSelectInput from "../../../Common/Select-Input/SelectInput";
import DynamicListUsers from "../../../Common/DynamicListUsers/DynamicListUsers";
import Button from "../../../Common/Button/Button";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CallStageIcon from "../../../Assets/Images/can-call-stage.svg";
import CallStageDarkIcon from "../../../Assets/Images/can-call-stage-dark.svg";
import TimeDarkIcon from "../../../Assets/Images/can-date-dark.svg";
import TimeIcon from "../../../Assets/Images/can-time.svg";
import industryDarkIcon from "../../../Assets/Images/industry-icon-filters-dark.svg";
import industryIcon from "../../../Assets/Images/industry-icon-filters-light.svg";
import candidateIcon from "../../../Assets/Images/user-icon-filters-light.svg";
import candidateDarkIcon from "../../../Assets/Images/user-icon-filters-dark.svg";
import moment from "moment";
import { CustomDatePickerModal } from "../../../Common/Date-Picker/DatePicker";
import calendarIcon from "../../../Assets/Images/calendarIcon.png";
import darkCalendarIcon from "../../../Assets/Images/calendar-dark-Icon.png";
import peopleIcon from "../../../Assets/Images/people-icon.svg";
import peopleDarkICON from "../../../Assets/Images/people-Dark.svg";
import DynamicMultipleSelectCheckmarks from "../../Multiple-Select-Checkmarks/MultipleSelectCheckmarks";
import { fetchCallStages } from "../../../Common/DynamicCallStage/DynamicCallStageMapping";
import DynamicAutocomplete from "../../../Common/AutoSuggestion/DynamicAutocomplete";
import { VYLABS_BASE_URL, SEARC_JOBS } from "../../../helper/url_helper";
import { post } from "../../../helper/Api_helper";
import { filterPayload } from "../../../Common/Common-Function/CommonFunctions";

const DATE_RANGE_OPTIONS = [
  { label: "Today", value: "Today" },
  { label: "Yesterday", value: "Yesterday" },
  { label: "This week", value: "This week" },
  { label: "This month", value: "This month" },
  { label: "Custom", value: "Custom" },
];

const DATE_RANGES = [
  { label: "Created", from: "from", to: "to" },
  { label: "Tracked", from: "trackingFrom", to: "trackingTo" },
];

const OWNERSHIP_OPTIONS = [
  { label: "Created by", value: "created" },
  { label: "Tracked by", value: "tracked" },
];

const textFieldContainerStyle = {
  marginBottom: 0,
  width: "100%",
};

const hiddenTextFieldLabelStyle = {
  display: "none",
  margin: 0,
};

const toStageArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const getPeriodDates = (period) => {
  const today = moment().startOf("day");
  if (period === "Today") return [today.toDate(), today.toDate()];
  if (period === "Yesterday") {
    const yesterday = today.subtract(1, "day").toDate();
    return [yesterday, yesterday];
  }
  if (period === "This week") return [today.clone().startOf("isoWeek").toDate(), today.toDate()];
  if (period === "This month") return [today.clone().startOf("month").toDate(), today.toDate()];
  return [null, null];
};

const CandidateFilterDrawer = ({
  isOpen = true,
  onClose = () => {},
  activeFilters = [],
  onApplyFull = () => {},
  onReset = () => {},
  isTrackedByMeActive = false,
  setIsTrackedByMeActive = () => {},
  isCreatedByMeActive = false,
  setIsCreatedByMeActive = () => {},
  setIsAllCandidatesActive = () => {},
  setTablePage = () => {},
  setSelectedCandidates = () => {},
  setIsSelectMode = () => {},
  setTrackedViewTab = () => {},
  skillsValue = "",
  onSkillsChange = () => {},
  minYearsOfExperienceValue = "",
  onMinYearsOfExperienceChange = () => {},
  maxYearsOfExperienceValue = "",
  onMaxYearsOfExperienceChange = () => {},
  industryValue = "",
  onIndustryChange = () => {},
  industryOptions = [],
  locationValue = "",
  onLocationChange = () => {},
  onSubmit = () => {},
  exportCandidateByFilter = () => {},
  onDraftChange = () => {},
  savedViewDraft = null,
  onCreateSavedView = () => {},
  isCreateSavedViewActive = false,
  onUpdateSavedView = () => {},
  isUpdateSavedViewActive = false,
  isUpdatingSavedView = false,
  isSavedViewSelected = false,
  savedFilterControl = null,
  isDarkMode = false,
}) => {
  const { userDetails, AdminAccess } = useContext(DynamicContext);
  const currentUserId = userDetails?.id || "";
  const { values, setFieldValue } = useFormik({
    initialValues: {
      users: "",
      job: null,
    },
  });
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const trackingFromDateRef = useRef(null);
  const trackingToDateRef = useRef(null);

  const [openSections, setOpenSections] = useState({
    candidateDetails: true,
    recruitment: true,
  });

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const [tempSkills, setTempSkills] = useState("");
  const [tempMinExp, setTempMinExp] = useState("");
  const [tempMaxExp, setTempMaxExp] = useState("");
  const [tempIndustry, setTempIndustry] = useState("");
  const [tempLocation, setTempLocation] = useState("");
  const [tempStages, setTempStages] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [tempDateRange, setTempDateRange] = useState("");
  const [dateFilterType, setDateFilterType] = useState("created");
  const [tempFromDate, setTempFromDate] = useState(null);
  const [tempToDate, setTempToDate] = useState(null);
  const [tempTrackingFromDate, setTempTrackingFromDate] = useState(null);
  const [tempTrackingToDate, setTempTrackingToDate] = useState(null);
  const [tempOwnershipType, setTempOwnershipType] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [openRangePicker, setOpenRangePicker] = useState(null);
  const [rangePickerInitialDate, setRangePickerInitialDate] = useState(null);
  const [rangePickerPosition, setRangePickerPosition] = useState({
    top: null,
    left: null,
  });
  const [jobListRes, setJobListRes] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadStageOptions = async () => {
      const stageMap = await fetchCallStages();
      if (!mounted) return;
      setStageOptions(
        Object.entries(stageMap).map(([value, label]) => ({ label, value })),
      );
    };

    loadStageOptions();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setTempSkills(skillsValue || "");
    setTempMinExp(minYearsOfExperienceValue || "");
    setTempMaxExp(maxYearsOfExperienceValue || "");
    setTempIndustry(
      industryValue ||
        activeFilters.find((f) => f.type === "industry")?.value ||
        "",
    );
    setTempLocation(
      locationValue ||
        activeFilters.find((f) => f.type === "location")?.value ||
        "",
    );

    const stageFilter = activeFilters.find((f) => f.type === "call_stage");
    const dateFilter = activeFilters.find((f) => f.type === "date");
    const fromDateFilter = activeFilters.find((f) => f.type === "from_date");
    const toDateFilter = activeFilters.find((f) => f.type === "to_date");
    const userFilter = activeFilters.find((f) => f.type === "user_id");

    setTempStages(toStageArray(stageFilter?.value));
    const trackingFrom = activeFilters.find((f) => f.type === "tracking_from_date")?.value;
    const trackingTo = activeFilters.find((f) => f.type === "tracking_end_date")?.value;
    const hasCreatedDates = Boolean(fromDateFilter?.value || toDateFilter?.value);
    const useTrackedDates = dateFilter?.dateFilterType === "tracked" ||
      (!hasCreatedDates && Boolean(trackingFrom || trackingTo));
    const [periodFrom, periodTo] = getPeriodDates(dateFilter?.value);
    setDateFilterType(dateFilter?.dateFilterType || (useTrackedDates ? "tracked" : "created"));
    setTempDateRange(dateFilter?.value || (hasCreatedDates || useTrackedDates ? "Custom" : ""));
    setTempFromDate(useTrackedDates ? null : fromDateFilter?.value || periodFrom);
    setTempToDate(useTrackedDates ? null : toDateFilter?.value || periodTo);
    setTempTrackingFromDate(useTrackedDates ? trackingFrom || periodFrom : null);
    setTempTrackingToDate(useTrackedDates ? trackingTo || periodTo : null);
    setOpenRangePicker(null);
    setRangePickerInitialDate(null);
    setRangePickerPosition({ top: null, left: null });

    if (isTrackedByMeActive) {
      setTempOwnershipType("tracked");
    } else if (isCreatedByMeActive) {
      setTempOwnershipType("created");
    } else {
      setTempOwnershipType("");
    }

    const initialUserId = AdminAccess ? userFilter?.value || "" : "";
    setFieldValue("users", initialUserId);
    const isCurrentUser = String(initialUserId) === String(currentUserId);
    setSelectedUser(
      initialUserId
        ? {
            value: initialUserId,
            label: isCurrentUser ? "Me" : userFilter?.label || String(initialUserId),
          }
        : null,
    );
  }, [
    isOpen,
    skillsValue,
    minYearsOfExperienceValue,
    maxYearsOfExperienceValue,
    industryValue,
    locationValue,
    activeFilters,
    isTrackedByMeActive,
    isCreatedByMeActive,
    currentUserId,
    AdminAccess,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!isOpen || !savedViewDraft) return;

    setTempSkills(savedViewDraft.skills || "");
    setTempMinExp(savedViewDraft.minExp || "");
    setTempMaxExp(savedViewDraft.maxExp || "");
    setTempIndustry(savedViewDraft.industry || "");
    setTempLocation(savedViewDraft.location || "");
    setTempStages(toStageArray(savedViewDraft.stage));
    const useTrackedDates = savedViewDraft.dateFilterType === "tracked" ||
      (!savedViewDraft.fromDate && !savedViewDraft.toDate &&
        Boolean(savedViewDraft.trackingFromDate || savedViewDraft.trackingToDate));
    const hasDates = Boolean(savedViewDraft.fromDate || savedViewDraft.toDate ||
      savedViewDraft.trackingFromDate || savedViewDraft.trackingToDate);
    const [periodFrom, periodTo] = getPeriodDates(savedViewDraft.dateRange);
    setDateFilterType(useTrackedDates ? "tracked" : "created");
    setTempDateRange(savedViewDraft.dateRange || (hasDates ? "Custom" : ""));
    setTempFromDate(useTrackedDates ? null : savedViewDraft.fromDate || periodFrom);
    setTempToDate(useTrackedDates ? null : savedViewDraft.toDate || periodTo);
    setTempTrackingFromDate(useTrackedDates ? savedViewDraft.trackingFromDate || periodFrom : null);
    setTempTrackingToDate(useTrackedDates ? savedViewDraft.trackingToDate || periodTo : null);
    setTempOwnershipType(savedViewDraft.ownershipType || "");
    const draftUserId = AdminAccess ? savedViewDraft.userId || "" : "";
    const isCurrentUser = String(draftUserId) === String(currentUserId);
    setFieldValue("users", draftUserId);
    setSelectedUser(
      draftUserId
        ? {
            value: draftUserId,
            label: isCurrentUser ? "Me" : savedViewDraft.userLabel || String(draftUserId),
          }
        : null,
    );
  }, [AdminAccess, currentUserId, isOpen, savedViewDraft, setFieldValue]);

  const candidateDetailsCount = useMemo(() => {
    let count = 0;
    if (tempSkills && String(tempSkills).trim()) count++;
    if (
      (tempMinExp && String(tempMinExp).trim()) ||
      (tempMaxExp && String(tempMaxExp).trim())
    )
      count++;
    if (tempIndustry && String(tempIndustry).trim()) count++;
    if (tempLocation && String(tempLocation).trim()) count++;
    return count;
  }, [tempSkills, tempMinExp, tempMaxExp, tempIndustry, tempLocation]);

  const recruitmentCount = useMemo(() => {
    let count = 0;
    if (tempOwnershipType) count++;
    if (tempStages.length > 0) count++;
    if (tempFromDate || tempToDate || tempTrackingFromDate || tempTrackingToDate) count++;
    if (AdminAccess && selectedUser !== null) count++;
    if (values.job !== null) count++;
    return count;
  }, [
    tempOwnershipType,
    tempStages,
    tempFromDate,
    tempToDate,
    tempTrackingFromDate,
    tempTrackingToDate,
    selectedUser,
    AdminAccess,
    values.job,
  ]);

  const totalActiveCount = candidateDetailsCount + recruitmentCount;

  useEffect(() => {
    if (!isOpen) return;

    onDraftChange?.({
      skills: tempSkills,
      minExp: tempMinExp,
      maxExp: tempMaxExp,
      industry: tempIndustry,
      location: tempLocation,
      stage: tempStages,
      dateRange: tempDateRange === "Custom" ? "" : tempDateRange,
      dateFilterType,
      fromDate: tempDateRange === "Custom" ? tempFromDate : null,
      toDate: tempDateRange === "Custom" ? tempToDate : null,
      trackingFromDate: tempDateRange === "Custom" ? tempTrackingFromDate : null,
      trackingToDate: tempDateRange === "Custom" ? tempTrackingToDate : null,
      ownershipType: tempOwnershipType,
      userId: AdminAccess ? selectedUser?.value || "" : "",
      userLabel: AdminAccess ? selectedUser?.label || "" : "",
    });
  }, [
    tempSkills,
    tempMinExp,
    tempMaxExp,
    tempIndustry,
    tempLocation,
    tempStages,
    tempDateRange,
    dateFilterType,
    tempFromDate,
    tempToDate,
    tempTrackingFromDate,
    tempTrackingToDate,
    tempOwnershipType,
    values.users,
    selectedUser,
    AdminAccess,
    isOpen,
    onDraftChange,
  ]);

  const handleReset = () => {
    setTempSkills("");
    setTempMinExp("");
    setTempMaxExp("");
    setTempIndustry("");
    setTempLocation("");
    setTempStages([]);
    setTempDateRange("");
    setDateFilterType("created");
    setTempFromDate(null);
    setTempToDate(null);
    setTempTrackingFromDate(null);
    setTempTrackingToDate(null);
    setOpenRangePicker(null);
    setRangePickerInitialDate(null);
    setRangePickerPosition({ top: null, left: null });
    setTempOwnershipType("");
    setFieldValue("users", "");
    setSelectedUser(null);
    setFieldValue("job", "");

    if (typeof onReset === "function") {
      onReset();
    }
  };

  const rangePickerFields = {
    from: {
      type: "from_date",
      ref: fromDateRef,
      value: tempFromDate,
      setValue: setTempFromDate,
    },
    to: {
      type: "to_date",
      ref: toDateRef,
      value: tempToDate,
      setValue: setTempToDate,
    },
    trackingFrom: {
      type: "tracking_from_date",
      ref: trackingFromDateRef,
      value: tempTrackingFromDate,
      setValue: setTempTrackingFromDate,
    },
    trackingTo: {
      type: "tracking_end_date",
      ref: trackingToDateRef,
      value: tempTrackingToDate,
      setValue: setTempTrackingToDate,
    },
  };
  const isInvalidRange = ({ from, to }) => {
    const start = rangePickerFields[from].value;
    const end = rangePickerFields[to].value;
    return Boolean(start && end && moment(start).isAfter(moment(end), "day"));
  };
  const selectedDateRange = DATE_RANGES[dateFilterType === "tracked" ? 1 : 0];
  const selectedFromDate = rangePickerFields[selectedDateRange.from].value;
  const selectedToDate = rangePickerFields[selectedDateRange.to].value;
  const hasInvalidDateRange = isInvalidRange(selectedDateRange) ||
    (tempDateRange === "Custom" && (!selectedFromDate || !selectedToDate));

  const changeDateFilterType = (nextType) => {
    setOpenRangePicker(null);
    setDateFilterType(nextType);
    setTempFromDate(nextType === "created" ? selectedFromDate : null);
    setTempToDate(nextType === "created" ? selectedToDate : null);
    setTempTrackingFromDate(nextType === "tracked" ? selectedFromDate : null);
    setTempTrackingToDate(nextType === "tracked" ? selectedToDate : null);
  };

  const changeDatePeriod = (period) => {
    setOpenRangePicker(null);
    setTempDateRange(period);
    if (period === "Custom") return;
    const [from, to] = getPeriodDates(period);
    rangePickerFields[selectedDateRange.from].setValue(from);
    rangePickerFields[selectedDateRange.to].setValue(to);
  };

  const openDateRangePicker = (pickerType) => {
    const targetRef = rangePickerFields[pickerType].ref;
    const rect = targetRef.current?.getBoundingClientRect();

    if (!rect) return;

    setRangePickerPosition({
      top: rect.bottom + 5,
      left: rect.left,
    });
    setRangePickerInitialDate(
      rangePickerFields[pickerType].value || new Date(),
    );
    requestAnimationFrame(() => setOpenRangePicker(pickerType));
  };

  const handleApply = () => {
    if (hasInvalidDateRange) return;
    onSkillsChange?.({ target: { value: tempSkills } });
    onMinYearsOfExperienceChange?.({ target: { value: tempMinExp } });
    onMaxYearsOfExperienceChange?.({ target: { value: tempMaxExp } });
    onIndustryChange?.({ target: { value: tempIndustry } });
    onLocationChange?.({ target: { value: tempLocation } });

    const filtersWithValues = [];
    if (tempStages.length > 0) {
      filtersWithValues.push({
        type: "call_stage",
        value: tempStages,
      });
    }
    if (tempDateRange && tempDateRange !== "Custom") {
      filtersWithValues.push({ type: "date", value: tempDateRange, dateFilterType });
    } else if (tempDateRange === "Custom") {
      for (const key of [selectedDateRange.from, selectedDateRange.to]) {
        const { type, value } = rangePickerFields[key];
        if (value) filtersWithValues.push({ type, value });
      }
    }
    if (tempIndustry && tempIndustry.trim()) {
      filtersWithValues.push({
        type: "industry",
        value: tempIndustry.trim(),
      });
    }
    if (tempLocation && tempLocation.trim()) {
      filtersWithValues.push({
        type: "location",
        value: tempLocation.trim(),
      });
    }
    if (values?.job) {
      filtersWithValues.push({
        type: "job_id",
        value: values.job.value,
      });
    }

    const selectedUserId = AdminAccess ? selectedUser?.value || "" : "";
    const selectedUserOption =
      selectedUser && String(selectedUser.value) === String(selectedUserId)
        ? selectedUser
        : null;
    if (tempOwnershipType === "tracked") {
      setIsAllCandidatesActive?.(false);
      setIsTrackedByMeActive?.(true);
      setIsCreatedByMeActive?.(false);
      if (selectedUserId) {
        if (!values.users) {
          setFieldValue("users", selectedUserId);
        }
        const existingFilter = activeFilters.find(
          (f) =>
            f.type === "user_id" && String(f.value) === String(selectedUserId),
        );
        filtersWithValues.push({
          type: "user_id",
          value: selectedUserId,
          label: selectedUserOption
            ? selectedUserOption.label
            : String(selectedUserId) === String(currentUserId)
              ? "Me"
              : existingFilter?.label || selectedUserId,
        });
      }
    } else if (tempOwnershipType === "created") {
      setIsAllCandidatesActive?.(false);
      setIsTrackedByMeActive?.(false);
      setIsCreatedByMeActive?.(true);
      if (selectedUserId) {
        if (!values.users) {
          setFieldValue("users", selectedUserId);
        }
        const existingFilter = activeFilters.find(
          (f) =>
            f.type === "user_id" && String(f.value) === String(selectedUserId),
        );
        filtersWithValues.push({
          type: "user_id",
          value: selectedUserId,
          label: selectedUserOption
            ? selectedUserOption.label
            : String(selectedUserId) === String(currentUserId)
              ? "Me"
              : existingFilter?.label || selectedUserId,
        });
      }
    } else {
      setIsAllCandidatesActive?.(true);
      setIsTrackedByMeActive?.(false);
      setIsCreatedByMeActive?.(false);
      if (selectedUserId) {
        if (!values.users) {
          setFieldValue("users", selectedUserId);
        }
        const existingFilter = activeFilters.find(
          (f) =>
            f.type === "user_id" && String(f.value) === String(selectedUserId),
        );
        filtersWithValues.push({
          type: "user_id",
          value: selectedUserId,
          label: selectedUserOption
            ? selectedUserOption.label
            : String(selectedUserId) === String(currentUserId)
              ? "Me"
              : existingFilter?.label || selectedUserId,
        });
      }
    }
    setTablePage?.(1);
    setSelectedCandidates?.([]);
    setIsSelectMode?.(false);
    setTrackedViewTab?.(0);
    const hasCandidateCriteria =
      tempDateRange ||
      (tempSkills && tempSkills.trim()) ||
      (tempMinExp && tempMinExp.trim()) ||
      (tempMaxExp && tempMaxExp.trim()) ||
      (tempIndustry && tempIndustry.trim()) ||
      (tempLocation && tempLocation.trim()) ||
      tempFromDate ||
      tempToDate ||
      tempTrackingFromDate ||
      tempTrackingToDate;

    onApplyFull?.(filtersWithValues, {
      hasCandidateCriteria: Boolean(hasCandidateCriteria),
      filterBy: tempOwnershipType || undefined,
    });

    if (hasCandidateCriteria && typeof onSubmit === "function") {
      onSubmit(1, {
        skills: tempSkills,
        minExp: tempMinExp,
        maxExp: tempMaxExp,
        industry: tempIndustry,
        loc: tempLocation,
        filters: filtersWithValues,
        filterBy: tempOwnershipType || undefined,
      });
    }

    if (
      !hasCandidateCriteria &&
      typeof exportCandidateByFilter === "function"
    ) {
      exportCandidateByFilter(
        filtersWithValues,
        tempOwnershipType === "tracked",
        tempOwnershipType === "created",
      );
    }

    onClose?.();
  };

  const dateRangePicker =
    openRangePicker && rangePickerPosition.top !== null
      ? createPortal(
          <CustomDatePickerModal
            initialDate={rangePickerInitialDate}
            maxDate={new Date()}
            disablePast={false}
            onOk={(date) => {
              setRangePickerPosition({ top: null, left: null });
              rangePickerFields[openRangePicker].setValue(date);
              setTempDateRange("Custom");
              setOpenRangePicker(null);
              setRangePickerInitialDate(null);
            }}
            onClose={() => {
              setOpenRangePicker(null);
              setRangePickerInitialDate(null);
              setRangePickerPosition({ top: null, left: null });
            }}
            top={rangePickerPosition.top}
            left={rangePickerPosition.left}
            zIndex={9999}
          />,
          document.body,
        )
      : null;

  async function getJobsList(searchData) {
    try {
      const res = await post(VYLABS_BASE_URL, SEARC_JOBS, filterPayload({
        limit: 50,
        page: 1,
        query: searchData,
      }));
      const options = res?.jobs.map((i) => ({
        label: i.job_role,
        value: i.id,
      }));
      setJobListRes(options);
    }
    catch (error) {
      console.error("error in getJobsList",error);
    }
  };

  return (
    <div className="candidate-filter-drawer">
      {savedFilterControl && (
        <div className="candidate-saved-view-filter">
          <label
            className="candidate-filter-label"
            htmlFor="candidate-saved-filter"
          >
            Saved filters
          </label>
          {savedFilterControl}
        </div>
      )}
      <div className="candidate-filter-drawer-body">
        <div className="candidate-filter-section">
          <div
            className="candidate-filter-section-header"
            onClick={() => toggleSection("candidateDetails")}
          >
            <span className="candidate-filter-section-title">
              CANDIDATE DETAILS
            </span>
            <div className="candidate-filter-section-meta">
              <span className="candidate-filter-badge">
                {candidateDetailsCount}
              </span>
              {openSections.candidateDetails ? (
                <KeyboardArrowUpIcon className="candidate-filter-chevron" />
              ) : (
                <KeyboardArrowDownIcon className="candidate-filter-chevron" />
              )}
            </div>
          </div>

          {openSections.candidateDetails && (
            <div className="candidate-filter-fields">
              <div className="candidate-filter-field">
                <label className="candidate-filter-label">
                  <PersonSearchOutlinedIcon fontSize="inherit" />
                  <span>Skills</span>
                </label>
                <DynamicTextField
                  name="skills"
                  className="candidate-filter-input"
                  containerStyle={textFieldContainerStyle}
                  labelStyle={hiddenTextFieldLabelStyle}
                  placeholder="Select skills"
                  value={tempSkills}
                  onChange={(e) => setTempSkills(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleApply();
                    }
                  }}
                />
              </div>

              <div className="candidate-filter-field">
                <label className="candidate-filter-label">
                  <WorkspacePremiumOutlinedIcon fontSize="inherit" />
                  <span>Experience</span>
                </label>
                <div className="candidate-filter-experience-row">
                  <DynamicTextField
                    name="min_experience"
                    className="candidate-filter-input"
                    containerStyle={textFieldContainerStyle}
                    labelStyle={hiddenTextFieldLabelStyle}
                    placeholder="Min. years"
                    value={tempMinExp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "");
                      setTempMinExp(val);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApply();
                      }
                    }}
                  />
                  <DynamicTextField
                    name="max_experience"
                    className="candidate-filter-input"
                    containerStyle={textFieldContainerStyle}
                    labelStyle={hiddenTextFieldLabelStyle}
                    placeholder="Max. years"
                    value={tempMaxExp}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9.]/g, "");
                      setTempMaxExp(val);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApply();
                      }
                    }}
                  />
                </div>
              </div>

              <div className="candidate-filter-field">
                <label className="candidate-filter-label">
                  {/* <BusinessCenterOutlinedIcon fontSize="inherit" /> */}
                  <img
                    src={isDarkMode ? industryDarkIcon : industryIcon}
                    alt="Industry"
                  />
                  <span>Industry</span>
                </label>
                <DynamicSelectInput
                  name="industry"
                  value={tempIndustry}
                  onChange={(e) => setTempIndustry(e.target.value)}
                  options={industryOptions}
                  defaultOption="Select industry"
                  wrapperStyle={{ width: "100%" }}
                  menuWidth="380px"
                  menuProps={{
                    sx: { zIndex: 1402 },
                    PaperProps: {
                      className: "candidate-filter-select-menu",
                      style: {
                        maxHeight: 224,
                        width: 380,
                        maxWidth: "calc(100vw - 32px)",
                        borderRadius: "8px",
                        border: "0.5px solid var(--border__color)",
                      },
                    },
                  }}
                  selectStyle={{
                    width: "100%",
                    height: "34px",
                    fontFamily: "Satoshi",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--header__color)",
                    borderRadius: "8px",
                  }}
                  selectSx={{
                    "& .MuiSelect-select": {
                      padding: "6px 32px 6px 10px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px !important",
                      borderColor:
                        "var(--search-input-section-border) !important",
                    },
                  }}
                  defaultOptionStyle={{ color: "#9aa0a6" }}
                />
              </div>

              <div className="candidate-filter-field">
                <label className="candidate-filter-label">
                  <LocationOnOutlinedIcon fontSize="inherit" />
                  <span>Location</span>
                </label>
                <div className="candidate-filter-selectlike">
                  <DynamicTextField
                    name="location"
                    className="candidate-filter-input"
                    containerStyle={textFieldContainerStyle}
                    labelStyle={hiddenTextFieldLabelStyle}
                    placeholder="Select location"
                    value={tempLocation}
                    onChange={(e) => setTempLocation(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleApply();
                      }
                    }}
                  />
                  <KeyboardArrowDownIcon className="candidate-filter-selectlike-icon" />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="candidate-filter-section">
          <div
            className="candidate-filter-section-header"
            onClick={() => toggleSection("recruitment")}
          >
            <span className="candidate-filter-section-title">RECRUITMENT ACTIVITY</span>
            <div className="candidate-filter-section-meta">
              <span className="candidate-filter-badge">{recruitmentCount}</span>
              {openSections.recruitment ? (
                <KeyboardArrowUpIcon className="candidate-filter-chevron" />
              ) : (
                <KeyboardArrowDownIcon className="candidate-filter-chevron" />
              )}
            </div>
          </div>

          {openSections.recruitment && (
            <div className="candidate-filter-fields">
              <div className="candidate-filter-field">
                <span className="candidate-filter-label" id="candidate-ownership-label">
                  <img
                    src={isDarkMode ? candidateDarkIcon : candidateIcon}
                    alt=""
                  />
                  <span>Show candidates</span>
                </span>
                <DynamicSelectInput
                  name="ownership_type"
                  value={tempOwnershipType}
                  onChange={(e) => setTempOwnershipType(e.target.value)}
                  options={OWNERSHIP_OPTIONS}
                  labelKey="label"
                  defaultOption="Select option"
                  defaultOptionStyle={{ color: "#9aa0a6" }}
                  wrapperStyle={{ width: "100%" }}
                  menuProps={{
                    sx: { zIndex: 1402 },
                    PaperProps: {
                      className: "candidate-filter-select-menu",
                    },
                  }}
                  selectStyle={{
                    width: "100%",
                    height: "34px",
                    fontFamily: "Satoshi",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--header__color)",
                    borderRadius: "8px",
                  }}
                  selectSx={{
                    "& .MuiSelect-select": {
                      padding: "6px 32px 6px 10px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px !important",
                      borderColor: "var(--search-input-section-border) !important",
                    },
                  }}
                />
              </div>

              {AdminAccess && (
                <div className="candidate-filter-field">
                  <label className="candidate-filter-label">
                    <img
                      src={isDarkMode ? peopleDarkICON : peopleIcon}
                      alt="Users"
                    />
                    <span>People</span>
                  </label>
                  <div style={{ width: "100%" }}>
                    <DynamicListUsers
                      value={selectedUser}
                      showMe
                      onChange={(value) => {
                        const selected = value
                          ? {
                              ...value,
                              label:
                                String(value.value) === String(currentUserId)
                                  ? "Me"
                                  : value.label,
                            }
                          : null;
                        setSelectedUser(selected);
                        setFieldValue("users", selected?.value || "");
                      }}
                      placeholder="Select User"
                      popupIcon={<KeyboardArrowDownIcon />}
                      usersSx={{
                        "& .MuiOutlinedInput-root": {
                          height: "34px",
                          borderRadius: "8px !important",
                          borderColor:
                            "var(--search-input-section-border) !important",
                          fontSize: "13px",
                        },
                      }}
                      usersComponentProps={{
                        popper: {
                          sx: {
                            zIndex: 1402,
                            "& .MuiAutocomplete-paper": {
                              backgroundColor: "var(--body__background)",
                              color: "var(--header__color)",
                              borderRadius: "8px",
                              boxShadow: "0 8px 22px rgba(15, 23, 42, 0.18)",
                              border: "1px solid var(--border__color)",
                            },
                            "& .MuiAutocomplete-listbox": {
                              maxHeight: "224px",
                            },
                            "& .MuiAutocomplete-option": {
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "var(--header__color)",
                            },
                            "& .MuiAutocomplete-noOptions": {
                              color: "#6860ee",
                            },
                            "& .MuiAutocomplete-loading": {
                              color: "#6860ee",
                              fontStyle: "italic",
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="candidate-filter-field">
                  <label className="candidate-filter-label">
                    <img
                      src={isDarkMode ? candidateDarkIcon : candidateIcon}
                      alt="Jobs"
                    />
                    <span>Jobs</span>
                  </label>
                  <div style={{ width: "100%" }}>
                    <DynamicAutocomplete
                      onOpen={() => getJobsList("")}
                      options={jobListRes}
                      value={values.job}
                      onChange={(data) => {
                        setFieldValue("job", data);
                      }}
                      onInputChange={(event, newInputValue, reason) => getJobsList(newInputValue)}
                      placeholder={"Select Job"}
                      popupIcon={<KeyboardArrowDownIcon />}
                      noOptionsText="No Jobs found."
                      sx={{
                        mt: "5px",
                        ".MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          height: "40px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "1px solid var(--border__color) !important",
                            borderRadius: "12px",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6860ee",
                            borderRadius: "12px",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#7a70f2",
                            borderRadius: "12px",
                          },
                        },
                        ".MuiInputBase-input": {
                          color: "var(--header__color)",
                          padding: "8px 12px !important",
                          height: "40px",
                          fontFamily: "Satoshi !important",
                        },
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            backgroundColor: "var(--body__background)",
                            color: "var(--header__color)",
                            borderRadius: "12px",
                            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                            border: "1px solid var(--border__color)",
                          },
                        },
                        listbox: {
                          sx: {
                            "& li": {
                              fontSize: "14px",
                              fontWeight: 500,
                              padding: "8px 16px",
                              color: "var(--header__color)",
                              "&:hover": {
                                backgroundColor: "rgba(0,0,0,0.04)",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>

              <div className="candidate-filter-field">
                <label className="candidate-filter-label">
                  <img
                    src={isDarkMode ? CallStageDarkIcon : CallStageIcon}
                    alt="Call Stage"
                  />
                  <span>Call Stage</span>
                </label>
                <DynamicMultipleSelectCheckmarks
                  options={stageOptions}
                  label=""
                  labelKey="label"
                  valueKey="value"
                  value={tempStages}
                  onChange={setTempStages}
                  placeholder="Select stages"
                  arrowIcon={KeyboardArrowDownIcon}
                  width="100%"
                  resetSearchTerm
                />
              </div>

              <div className="candidate-date-card">
                <span className="candidate-filter-label candidate-date-card-title">
                  <img src={isDarkMode ? TimeDarkIcon : TimeIcon} alt="" />Date range
                </span>
                <span className="candidate-filter-label" id="candidate-date-type-label">Filter by</span>
                <div className="candidate-ownership-toggle" role="radiogroup" aria-labelledby="candidate-date-type-label">
                  {[{ value: "created", label: "Created date" }, { value: "tracked", label: "Tracked date" }].map(({ value, label }) => (
                    <label className="candidate-ownership-option" key={value}>
                      <input type="radio" name="date_filter_type" value={value}
                        checked={dateFilterType === value}
                        onChange={() => changeDateFilterType(value)} />
                      <span>{label}</span>
                    </label>
                  ))}
              </div>
                <span className="candidate-filter-label" id="candidate-date-period-label">Period</span>
                <div className="candidate-date-periods" role="group" aria-labelledby="candidate-date-period-label">
                  {DATE_RANGE_OPTIONS.map(({ label, value }) => (
                    <button type="button" key={value} aria-pressed={tempDateRange === value}
                      className="candidate-date-period" onClick={() => changeDatePeriod(value)}>{label}</button>
                  ))}
                </div>
                {tempDateRange === "Custom" && (
                  <div className="candidate-date-range-wrapper">
                    {[selectedDateRange.from, selectedDateRange.to].map((pickerType, index) => {
                      const { ref, value } = rangePickerFields[pickerType];
                      const label = index === 0 ? "From" : "To";
                      return (
                        <div className="candidate-filter-field" key={label}>
                          <span className="candidate-filter-label">{label}</span>
                          <button ref={ref} type="button" aria-label={selectedDateRange.label + " " + label + " date"}
                            className="candidate-date-range-control"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); openDateRangePicker(pickerType); }}>
                            <img alt="" src={isDarkMode ? darkCalendarIcon : calendarIcon} className="candidate-date-range-icon" />
                            <span className={!value ? "is-placeholder" : ""}>
                              {value ? moment(value).format("DD MMM YYYY") : label + " date"}
                    </span>
                  </button>
                </div>
                      );
                    })}
                  </div>
                )}
                {isInvalidRange(selectedDateRange) ? (
                  <span role="alert" className="candidate-date-error">From date must be on or before To date.</span>
                ) : tempDateRange === "Custom" && (!selectedFromDate || !selectedToDate) ? (
                  <span className="candidate-date-summary">Select both From and To dates.</span>
                ) : selectedFromDate && selectedToDate ? (
                  <span className="candidate-date-summary">
                    Showing candidates {dateFilterType} between {moment(selectedFromDate).format("D MMM")} and {moment(selectedToDate).format("D MMM YYYY")}.
                  </span>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="candidate-filter-drawer-footer">
        <button
          type="button"
          className="candidate-filter-reset-btn"
          onClick={handleReset}
        >
          Reset
        </button>
        <div className="candidate-filter-footer-actions">
          <Button
            type="button"
            className="candidate-filter-create-btn"
            label={isSavedViewSelected ? "Update filter" : "Save filter"}
            event={isSavedViewSelected ? onUpdateSavedView : onCreateSavedView}
            disabled={hasInvalidDateRange || (isSavedViewSelected
              ? !isUpdateSavedViewActive : !isCreateSavedViewActive)}
            loading={isSavedViewSelected && isUpdatingSavedView}
            backgroundColor="transparent"
            color="#5e4bea"
            border="1px solid #6860ee"
            hoverStyles={{ backgroundColor: "rgba(104, 96, 238, 0.08)" }}
          />
          <Button
            type="button"
            className="candidate-filter-apply-btn"
            label={`Apply filters (${totalActiveCount})`}
            event={handleApply}
            disabled={hasInvalidDateRange}
            backgroundColor="#5e4bea"
            hoverStyles={{ backgroundColor: "#574ee8" }}
          />
        </div>
      </div>
      {dateRangePicker}
    </div>
  );
};

CandidateFilterDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  activeFilters: PropTypes.array,
  onApplyFull: PropTypes.func,
  onReset: PropTypes.func,
  isTrackedByMeActive: PropTypes.bool,
  setIsTrackedByMeActive: PropTypes.func,
  isCreatedByMeActive: PropTypes.bool,
  setIsCreatedByMeActive: PropTypes.func,
  setIsAllCandidatesActive: PropTypes.func,
  setTablePage: PropTypes.func,
  setSelectedCandidates: PropTypes.func,
  setIsSelectMode: PropTypes.func,
  setTrackedViewTab: PropTypes.func,
  skillsValue: PropTypes.string,
  onSkillsChange: PropTypes.func,
  minYearsOfExperienceValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onMinYearsOfExperienceChange: PropTypes.func,
  maxYearsOfExperienceValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onMaxYearsOfExperienceChange: PropTypes.func,
  industryValue: PropTypes.string,
  onIndustryChange: PropTypes.func,
  industryOptions: PropTypes.array,
  locationValue: PropTypes.string,
  onLocationChange: PropTypes.func,
  onSubmit: PropTypes.func,
  exportCandidateByFilter: PropTypes.func,
  onDraftChange: PropTypes.func,
  savedViewDraft: PropTypes.object,
  onCreateSavedView: PropTypes.func,
  isCreateSavedViewActive: PropTypes.bool,
  onUpdateSavedView: PropTypes.func,
  isUpdateSavedViewActive: PropTypes.bool,
  isUpdatingSavedView: PropTypes.bool,
  isSavedViewSelected: PropTypes.bool,
  savedFilterControl: PropTypes.node,
  isDarkMode: PropTypes.bool,
};

export default CandidateFilterDrawer;
